import { memo, ReactElement } from 'react';
import { Grid, Typography } from '@mui/material';
import { IComponentCss, unreachableResultForSwitchCaseValidation } from '@tap/mui-ui';
import { Link as MuiLink, IconButton, Tooltip } from '@mui/material';
import { Link } from 'react-router';
import { DocumentCode, IApiRelationLink } from '@tap/data-providers';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import AllInboxOutlinedIcon from '@mui/icons-material/AllInboxOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import InsertLinkOutlinedIcon from '@mui/icons-material/InsertLinkOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import NorthIcon from '@mui/icons-material/North';
import SouthIcon from '@mui/icons-material/South';
import { StatusChip, StatusStyleCode, StatusChipVariant } from '@tap/tap-ui';
import { useDocuemtnSchema } from '../../context';

/**
 *  Список связанных ссылок (итем - страчка в списке)
 */

const css: IComponentCss = {
    item: {
        height: '34px',
        position: 'relative',
        padding: '3px 35px 4px 16px',
        borderBottom: '1px solid #e0e0e0',
        alignItems: 'center',
        transition: 'background-color 50ms',
        '&:hover': {
            backgroundColor: '#F5F5F5',
            '& .deleteButton': {
                display: 'inline-flex',
            },
        },
    },
    itemIcon: {
        color: '#5472d3',
        fontSize: '19px',
        marginBottom: '-4px',
        marginRight: '4px',
    },
    itemCodeWrap: {
        textWrap: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        lineHeight: '21px',
    },
    itemCode: {
        fontWeight: 400,
        fontSize: '14px',
        margin: '0px',
        padding: '0px',
        lineHeight: '21px',
        marginRight: '4px',
    },
    itemTitleWrap: {
        marginRight: 'auto',
        lineHeight: '21px',
        overflow: 'hidden',
    },
    deleteButton: {
        position: 'absolute',
        right: 0,
        display: 'none',
    },
    priorityIcon: {
        borderRadius: '4px',
        color: '#ffffff',
        fontSize: '16px',
        marginBottom: '-3px',
        marginLeft: '1px',
        marginRight: '8px',
    },
    highPriorityIcon: {
        backgroundColor: '#e57373',
    },
    mediumPriorityIcon: {
        backgroundColor: '#90a4ae',
    },
    lowPriorityIcon: {
        backgroundColor: '#81c784',
    },
}

enum Priority {
    High = 1,
    Medium = 2,
    Low = 3,
}

const getIcon = (
    documentCode: string,
): ReactElement<any, any> => {
    switch (documentCode) {
        case DocumentCode.WO:
            return <InboxOutlinedIcon sx={css.itemIcon} />;
        case DocumentCode.SALE:
            return <AllInboxOutlinedIcon sx={css.itemIcon} />;
        case DocumentCode.DSG:
            return <CalendarMonthIcon sx={css.itemIcon} />;
        case DocumentCode.MES:
            return <WidgetsOutlinedIcon sx={css.itemIcon} />;
        default:
            return <InsertLinkOutlinedIcon sx={css.itemIcon} />;
    }

    return unreachableResultForSwitchCaseValidation<JSX.Element>(documentCode);
}

type IRelationLinkListItemProps = {
    link: IApiRelationLink,
    handleClickOnDeleteLinkBtn: (link: IApiRelationLink) => void,
}


const getPriorityIcon = (priorityState: number): ReactElement | number => {
    switch (priorityState) {
        case Priority.High:
            return <NorthIcon sx={{ ...css.priorityIcon, ...css.highPriorityIcon }} />
        case Priority.Medium:
            return <HorizontalRuleIcon sx={{ ...css.priorityIcon, ...css.mediumPriorityIcon }} />
        case Priority.Low:
            return <SouthIcon sx={{ ...css.priorityIcon, ...css.lowPriorityIcon }} />
        default:
            return priorityState;
    }
}


const getPriorityTooltip = (priorityState: number): string | undefined => {
    switch (priorityState) {
        case Priority.High:
            return 'Высокий'
        case Priority.Medium:
            return 'Средний'
        case Priority.Low:
            return 'Низкий'
        default:
            return undefined;
    }
}


export const RelationLinkListItem = memo(({
    link,
    handleClickOnDeleteLinkBtn,
}: IRelationLinkListItemProps) => {

    const documentCode = link.relatedDocument.documentKey.split('-')[0];
    const statusCode = link.relatedDocument.data?.status?.code;

    const documentSchema = useDocuemtnSchema(documentCode);

    const styleCode = link.relatedDocument.data?.status?.props?.styleCode
        ?? documentSchema.settings?.statusList?.find((s) => s.code === statusCode)?.styleCode ?? undefined;

    return (
        <>
            <Grid
                wrap='nowrap'
                container
                sx={css.item}
            >
                <Grid item>
                    {getIcon(documentCode)}
                </Grid>

                <Grid item sx={css.itemCodeWrap}>
                    <MuiLink
                        noWrap
                        component={Link}
                        sx={css.itemCode}
                        to={`/${link.relatedDocument.documentKey}`}
                    >
                        {link.relatedDocument.documentKey}:
                    </MuiLink>
                </Grid>

                <Grid item sx={css.itemTitleWrap}>
                    <Typography noWrap>{link.relatedDocument.title}</Typography>
                </Grid>

                {link.relatedDocument?.data &&
                    <Grid item>
                        <Grid container>
                            {!!link.relatedDocument.data?.priority &&
                                <Tooltip
                                    disableFocusListener={!getPriorityTooltip(link.relatedDocument.data.priority)}
                                    title={getPriorityTooltip(link.relatedDocument.data.priority)}
                                >
                                    <Typography>
                                        {getPriorityIcon(link.relatedDocument.data.priority)}
                                    </Typography>
                                </Tooltip>
                            }

                            {link.relatedDocument.data?.status &&
                                <StatusChip
                                    styleCode={styleCode as StatusStyleCode}
                                    statusName={link.relatedDocument.data.status.name}
                                    variant={StatusChipVariant.Outlined}
                                />
                            }
                        </Grid>
                    </Grid>
                }

                <IconButton
                    size='small'
                    sx={css.deleteButton}
                    className='deleteButton'
                    onClick={() => handleClickOnDeleteLinkBtn(link)}
                >
                    <CloseOutlinedIcon fontSize='small' />
                </IconButton>
            </Grid>
        </>
    )
}, (prevProps, nextProps) => {
    return prevProps.link.id === nextProps.link.id;
});

